<template>
  <v-card flat>
    <v-dialog
      v-model="showDeleteInput"
      max-width="480px"
    >
      <videon-card heading="Delete Input" showClose="true" @close="showDeleteInput = false">
        <v-card-text>
          <v-form @submit.prevent="confirmDeleteInput">
            <p class="subtitle-1 mt-3">
              Are you sure you want to delete this input?
            </p>
            <div class="text-right">
              <v-btn
                class="ma-1"
                color="secondary"
                @click="showDeleteInput = false"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="confirmDeleteInput"
              >
                Delete
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <v-dialog
      v-model="showNewInput"
      max-width="700px"
    >
      <videon-card heading="Create Input" showClose="true" @close="showNewInput = false">
        <v-card-text>
          <v-form @submit.prevent="createInput">
            <v-row class="mb-3">
              <v-col cols="8">
                <v-select
                  v-model="newInput.type"
                  :items="newInputTypes"
                  @change="newInputTypeChange"
                  label="Input Type"
                  outlined
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
            
            <div class="mt-5" v-if="newInput">
              <bird-dog-decoder-document v-if="newInput.type == 'birddog_decoder'" :input="newInput" :documents="documents" :device_guid="device_guid" :canEdit="canEdit" :isNew="true" @configUpdate="newInputUpdate" :key="newInput.id"/>
            </div>
            
            <div class="text-right">
              <v-btn
                class="ma-1"
                color="secondary"
                @click="showNewInput = false"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="createInput"
                :disabled="newInputHasValidationErrors"
              >
                Create Input
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    
    <v-card-title>
      Input Configuration
      <v-spacer/>
      <v-btn
        small
        color="primary"
        outlined
        tooltip="New Input"
        @click="newInputShow()"
        :disabled="!canEdit || !hasFeature('daemon.input.video.limit', instanceCount)"
      >
        <v-icon>mdi-plus</v-icon> Create Input
      </v-btn>
    </v-card-title>
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="documents"
        :single-expand="true"
        :expanded.sync="expanded"
        item-key="id"
        :items-per-page="100"
        show-expand
        hide-default-footer
        @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
      >
        <template
          v-slot:item.id="{ item }"
        >
          <span :class="(item.new) ? 'text--disabled' : ''">{{ inputName(item) }}</span>
        </template>
        <template
          v-slot:item.type="{ item }"
        >
          <span :class="(item.new) ? 'text--disabled' : ''">{{ inputType(item) }}</span>
        </template>
        <template
          v-slot:item.status="{ item }"
        >
          <span v-if="item.new" class="text--disabled">Not Created</span>
          <span v-else-if="item.srt_input_connected" class="primary--text">SRT Input Connected</span>
          <span v-else class="error--text">SRT Input Not Connected</span>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="px-8 pt-8 pb-5">
            <bird-dog-decoder-document v-if="item.type == 'birddog_decoder'" :input="item" :documents="documents" :device_guid="device_guid" :canEdit="canEdit" @configUpdate="configUpdate" @deleteInput="deleteInput(item)" :key="item.id"/>
            <generic-input-document v-else :input="item" :documents="documents" :device_guid="device_guid" :canEdit="canEdit" @configUpdate="configUpdate" @deleteInput="deleteInput(item)" :key="item.id"/>
          </td>
        </template>
      </v-data-table>
      
      <div class="mt-5 text-right" v-if="canEdit">
        <v-btn
          color="secondary"
          text
          class="ma-1"
          @click="revertShadow()"
          :disabled="!hasChanges"
        >
          Revert
        </v-btn>
        <v-btn
          color="primary"
          class="ma-1"
          @click="saveShadow()"
          :disabled="hasChanges == false || hasValidationError == true"
        >
          Save
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>
<script>
  import { mapGetters } from 'vuex'
  import ShadowMixins from '../mixins/mixins.js'
  
  import GenericInputDocument from './documents/GenericInputDocument.vue'
  import BirdDogDecoderDocument from './documents/BirdDogDecoderDocument.vue'
  
  export default {
    name: 'DecoderInputs',
    
    components: {
      GenericInputDocument,
      BirdDogDecoderDocument
    },
    
    mixins:[ShadowMixins],
    
    props: ['deviceShadow', 'device_guid', 'device'],
    
    data() {
      return {
        expanded: [],
        
        documents: [],
        hasChanges: false,
        expectingChanges: false,
        
        hasValidationError: false,
        
        showDeleteInput: false,
        selectedInput: false,
        
        showNewInput: false,
        
        newInput:false,
        newInputHasValidationErrors: false,
        
        headers: [
          { text: 'Name', align: 'start', value: 'id', sortable: true },
          { text: 'Input Type', align: 'start text-capitalize', value: 'type', sortable: true },
          { text: 'Input Status', align: 'start', value: 'status', sortable: true },
        ],
      }
    },
    watch: {
      deviceShadow: {
        handler() {
          // only overwrite if we dont have any documents to start with
          if (this.expectingChanges || this.expanded.length == 0 || this.documents.length == 0) {
            var documents = this.deviceShadow.editableStateCopy()
            if (documents) {
              this.documents = documents
              
              // reset expectingChanges
              setTimeout(() => {
                this.expectingChanges = false
              }, 5000)
            }
          }
        }, 
        deep: true
      }
    },
    computed: {
      ...mapGetters('user', ['isDeviceAdmin']),
      
      canEdit() {
        if (!this.device.hasFeature('services.cloud.device_config_tab.enabled') && !this.isDeviceAdmin) {
          return false
        }
        
        return this.device.canEdit()
      },
      
      newInputTypes() {
        var newInputTypes = [
          { 'id': false, 'value': 'srt', 'text': 'SRT' }
        ]
        
        return newInputTypes
      },
    },
    
    mounted() {
      var documents = this.deviceShadow.editableStateCopy()
      if (documents) {
        this.documents = documents
        this.hasChanges = false
      }
    },
    
    methods: {
      inputName(item) {
        if (item.config && item.config.name) {
          return this.$helpers.htmlDecode(item.config.name)
        } else if (item.new) {
          return 'New Input'
        }
        return 'Input #' + item.id
      },
      
      inputType(item) {
        if (item.type) {
          return this.$helpers.getLabel(item.type)
        }
        return 'Hardware'
      },
      
      newInputShow() {
        this.newInput =  {  'id': 'new-' +  new Date().getTime(), 'type': 'srt', 'new': true }
        this.showNewInput = true
      },
      
      newInputTypeChange() {
        console.log('DecoderInputs newInputTypeChange', this.newInput)
        this.$delete(this.newInput, 'config')
      },
      
      newInputUpdate(config, hasValidationError = false) {
        console.log('DecoderInputs newInputUpdate', config, hasValidationError)
        this.newInput.config = config
        this.newInputHasValidationErrors = hasValidationError
      },
      
      createInput() {
        if (!this.newInputHasValidationErrors) {
          // fix for transient vars
          if (!this.newInput.config.name) {
            this.newInput.config.name = ''
          }
          
          console.log('DecoderInputs createInput', this.newInput)
          
          this.documents.push(Object.assign({}, this.newInput))
          this.hasChanges = true
          this.expanded = []
          
          this.showNewInput = false
        }
      },
      
      deleteInput(item) {
        this.selectedInput = item
        this.showDeleteInput = true
      },
      
      confirmDeleteInput() {
        var inputIndex = this.documents.indexOf(this.selectedInput)
        if (inputIndex !== -1) {
          console.log('DecoderInputs confirmDeleteInput deleting input', this.selectedInput)
          
          this.documents.splice(inputIndex, 1)
          this.hasChanges = true
          this.expanded = []
        }
        this.showDeleteInput = false
      },
      
      
      
      configUpdate(config, hasValidationError = false) {
        this.hasChanges = true
        this.hasValidationError = hasValidationError
      },
      
      saveShadow() {
        var state = this.documents.map((input) => {
          var newInput = {'id': (input.new) ? false : input.id, 'type': input.type, 'config': input.config}
          
          return newInput
        })
        
        this.deviceShadow.saveShadow(state)
        this.expanded = []
        this.hasChanges = false
        this.expectingChanges = true
      },
      
      revertShadow() {
        this.documents = this.deviceShadow.editableStateCopy()
        this.hasChanges = false
      }
    }
  }
</script>